import * as React from 'react';

export type ReleaseAsset = { url: string; file: string; size?: number };
export type Release = {
  version: string;
  releasedAt?: string;
  windows?: { x64?: ReleaseAsset };
  mac?: { arm64?: ReleaseAsset; x64?: ReleaseAsset };
};

/**
 * Reads `latest.json`, written by Receipt-Desktop's release workflow next to the installers
 * (public S3 bucket). Fetched at runtime so a new release appears without redeploying this site.
 */
const RELEASES_URL = (process.env.REACT_APP_RELEASES_URL || '').replace(/\/$/, '');

export const useLatestRelease = () => {
  const [release, setRelease] = React.useState<Release | null>(null);
  const [status, setStatus] = React.useState<'loading' | 'ready' | 'unavailable'>(RELEASES_URL ? 'loading' : 'unavailable');

  React.useEffect(() => {
    if (!RELEASES_URL) return;
    let cancelled = false;
    fetch(`${RELEASES_URL}/latest.json`, { cache: 'no-cache' })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: Release) => { if (!cancelled) { setRelease(data); setStatus('ready'); } })
      .catch(() => { if (!cancelled) setStatus('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  return { release, status };
};

export type Platform = 'windows' | 'mac' | 'other';

/** Best-effort OS/CPU detection to pick the primary download button. */
export const useVisitorPlatform = () => {
  const [platform, setPlatform] = React.useState<Platform>('other');
  const [macArch, setMacArch] = React.useState<'arm64' | 'x64'>('arm64');

  React.useEffect(() => {
    const ua = navigator.userAgent;
    const mobile = /Android|iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (mobile) setPlatform('other');
    else if (/Windows/i.test(ua)) setPlatform('windows');
    else if (/Macintosh|Mac OS X/i.test(ua)) setPlatform('mac');

    // Safari can't tell Apple Silicon from Intel; Chromium can. Default to Apple Silicon (all Macs since 2020).
    const uaData = (navigator as any).userAgentData;
    uaData?.getHighEntropyValues?.(['architecture'])
      .then((v: { architecture?: string }) => { if (v.architecture === 'x86') setMacArch('x64'); })
      .catch(() => {});
  }, []);

  return { platform, macArch };
};

export const formatSize = (bytes?: number) => (bytes ? `${Math.round(bytes / 1024 / 1024)} MB` : '');
